import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  RktFormFieldConfig,
  formDatepicker,
  formInput,
  formInputPhone,
  formInputSecure,
  formRadioGroup,
  formRow,
  formSelectCountry,
} from '@rocketfinancialcorp/rocket-ui/form';

import { Patterns } from '@shared/components';
import { socialIdExpressions, socialIdPatternMessage } from '@shared/components/entity/entity-form-fields/identity-field-expressions';
import {
  IDENTITY_COUNTRY_RESTRICTIONS,
  PHONE_COUNTRY_RESTRICTIONS,
  RESTRICTED_PHONE_MESSAGE,
  SOCIAL_ID_FORMATS,
} from '@shared/constants/identity-formats';
import { Beneficiary } from '@shared/models';
import { socialIdValidatorFor } from '@shared/validators';

import { getAddressFields } from './beneficiary-form-utils';

const disableUnverifiedFieldOption = {
  expressions: {
    'templateOptions.disabled': (field: FormlyFieldConfig) => {
      const { actionType, verificationStatus } = field.model;
      return verificationStatus?.status !== 'UNVERIFIED' && actionType === 'edit';
    },
  },
};

export const editFormFields: RktFormFieldConfig[] = [
  formRow([
    formInput({
      key: 'firstName',
      label: 'First Name',
      props: {
        pattern: Patterns.GENERIC_NAME,
        required: true,
        minLength: 1,
        maxLength: 40,
      },
      ...disableUnverifiedFieldOption,
      validation: { messages: { pattern: 'Enter a valid first name' } },
    }),
    formInput({
      key: 'middleName',
      label: 'Middle Name',
      props: {
        pattern: Patterns.GENERIC_NAME,
        minLength: 1,
        maxLength: 40,
      },
      ...disableUnverifiedFieldOption,
      validation: { messages: { pattern: 'Enter a valid middle name' } },
    }),
    formInput({
      key: 'lastName',
      label: 'Last Name',
      props: {
        pattern: Patterns.GENERIC_NAME,
        required: true,
        minLength: 1,
        maxLength: 40,
      },
      ...disableUnverifiedFieldOption,
      validation: { messages: { pattern: 'Enter a valid last name' } },
    }),
  ]),
  formRow([
    formInput({
      key: 'primaryEmail',
      label: 'Email address',
      props: { pattern: Patterns.EMAIL, type: 'email', required: false, disabled: false, minLength: 1, maxLength: 100 },
      validation: {
        messages: {
          pattern: 'Enter a valid email',
        },
      },
    }),
    formInputPhone({
      key: 'primaryPhoneNumber',
      label: 'Phone Number',
      className: 'half-row-field',
      props: {
        required: false,
        minLength: 10,
        maxLength: 40,
        // Canada shares the NANP country code with the US, so a Canadian mobile parses as +1 and is rejected
        // outright unless 'CA' is named here. Beneficial-owner mobile numbers are an explicit AC.
        countryRestrictions: PHONE_COUNTRY_RESTRICTIONS,
        restrictedCountryMessage: RESTRICTED_PHONE_MESSAGE,
      },
      validators: { validation: [{ name: 'isValidPhoneNumber' }] },
    }),
  ]),
  formRow([
    formDatepicker({
      key: 'dateOfBirth',
      label: 'Date Of Birth',
      props: { required: false, placeholder: 'MM/DD/YY' },
      ...disableUnverifiedFieldOption,
    }),
    formInput({
      key: 'cityOfBirth',
      label: 'City of Birth',
      props: { pattern: Patterns.PLACE_OF_BIRTH, required: false, maxLength: 60 },
      validation: {
        messages: {
          pattern: 'Enter a valid place of birth',
        },
      },
    }),
    formSelectCountry({ key: 'countryOfBirth', label: 'Country of Birth', props: { placeholder: 'Select' } }),
  ]),
  formRow([
    formInput({
      key: 'title',
      label: 'Job Title',
      props: {
        pattern: Patterns.GENERIC_NAME,
        minLength: 1,
        maxLength: 100,
      },
      validation: { messages: { pattern: 'Enter a valid job title' } },
    }),
  ]),

  formRow([
    formRadioGroup({
      key: 'sex',
      label: 'Sex',
      className: 'single-row-radio-group',
      props: {
        options: [
          { label: 'Male', value: 'MALE' },
          { label: 'Female', value: 'FEMALE' },
          { label: 'Non-Binary', value: 'NONBINARY' },
        ],
      },
    }),
  ]),
  formRow([
    formRadioGroup({
      key: 'controller',
      label: 'Controller',
      className: 'single-row-radio-group',
      props: {
        required: false,
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },
    }),
  ]),
];

export const editOwningFormFields = (beneficary?: Beneficiary) => {
  return [
    formRow([
      formInput({
        key: 'socialId',
        label: SOCIAL_ID_FORMATS.USA.label,
        props: {
          required: false,
          pattern: SOCIAL_ID_FORMATS.USA.pattern,
          maxLength: SOCIAL_ID_FORMATS.USA.maxLength,
          minLength: 9,
          maskString: SOCIAL_ID_FORMATS.USA.mask,
          placeholder: SOCIAL_ID_FORMATS.USA.mask,
        },
        validators: {
          validation: [socialIdValidatorFor('socialIdCountryCode')],
        },
        validation: {
          messages: { pattern: socialIdPatternMessage() },
        },
        expressions: {
          hide: '!!model.maskedSocialId',
          ...socialIdExpressions(),
        },
      }),
      formInputSecure({
        key: 'socialId',
        label: SOCIAL_ID_FORMATS.USA.label,
        className: beneficary?.verificationStatus?.status !== 'UNVERIFIED' ? 'secured-input-field field-disable' : 'secured-input-field',
        props: {
          required: false,
          pattern: SOCIAL_ID_FORMATS.USA.pattern,
          maxLength: SOCIAL_ID_FORMATS.USA.maxLength,
          minLength: 9,
          maskString: SOCIAL_ID_FORMATS.USA.mask,
          placeholder: SOCIAL_ID_FORMATS.USA.mask,
          securedValuePrefix: SOCIAL_ID_FORMATS.USA.securedValuePrefix,
        },
        validators: {
          validation: [socialIdValidatorFor('socialIdCountryCode')],
        },
        validation: {
          messages: { pattern: socialIdPatternMessage() },
        },
        expressions: {
          hide: '!model.maskedSocialId',
          ...socialIdExpressions(),
        },
      }),
      formSelectCountry({
        key: 'socialIdCountryCode',
        label: 'Social ID Issuing Country',
        props: { placeholder: 'Select', countryRestrictions: IDENTITY_COUNTRY_RESTRICTIONS, required: false },
      }),
      formSelectCountry({
        key: 'citizenshipCountryCode',
        label: 'Citizenship Country',
        props: { placeholder: 'Select' },
      }),
    ]),
    formRow([
      formInput({
        key: 'ownershipPercentage',
        label: 'Percentage of Business owned',
        className: 'percentage-owned-row-field',
        props: {
          pattern: Patterns.PERCENT100,
        },
        validation: { messages: { pattern: 'Must be less or equal to 100' } },
      }),
    ]),
  ];
};

export const editAddressFormFields = ({
  fieldLinkClick,
  isEdit,
}: {
  fieldLinkClick: (type: 'Mailing' | 'Shipping') => void;
  isEdit: boolean;
}): RktFormFieldConfig[] => {
  return [
    ...getAddressFields({ type: 'Physical', key: 'physicalAddress', isRequired: isEdit, isEdit }),
    ...getAddressFields({
      type: 'Mailing',
      key: 'mailingAddress',
      isRequired: isEdit,
      isEdit,
      fieldLinkClick: () => fieldLinkClick('Mailing'),
    }),
    ...getAddressFields({
      type: 'Shipping',
      key: 'shippingAddress',
      isRequired: isEdit,
      isEdit,
      fieldLinkClick: () => fieldLinkClick('Shipping'),
    }),
  ];
};
